import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import InitialsAvatar from '../components/InitialsAvatar';

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  const [searchUrl, setSearchUrl] = useState(
    initialQuery.length >= 2 ? `/api/search?term=${encodeURIComponent(initialQuery)}&type=all` : null
  );

  const { data, loading } = useApi(searchUrl);
  const results = data?.results || [];

  useEffect(() => {
    const q = searchParams.get('q') || '';
    setQuery(q);
    if (q.length >= 2) {
      setSearchUrl(`/api/search?term=${encodeURIComponent(q)}&type=all`);
    } else {
      setSearchUrl(null);
    }
  }, [searchParams]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`, { replace: true });
    }
  };

  const players = results.filter((r) => r.type === 'player');
  const clubs = results.filter((r) => r.type === 'club');
  const leagues = results.filter((r) => r.type === 'league');

  const ResultGroup = ({ title, icon, items, renderItem }) => {
    if (items.length === 0) return null;
    return (
      <div className="space-y-3">
        <h3 className="font-label text-[11px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] flex items-center gap-2">
          <span className="material-symbols-outlined text-base">{icon}</span>
          {title} ({items.length})
        </h3>
        <div className="space-y-2">{items.map(renderItem)}</div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <header>
        <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">
          Players, Clubs &amp; Leagues
        </p>
        <h2 className="font-expanded text-3xl sm:text-4xl font-headline font-extrabold tracking-tight text-on-surface mb-4">
          Search
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="relative max-w-2xl">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none">search</span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full min-h-[56px] bg-surface-container-lowest border border-outline-variant/30 pl-12 pr-4 py-4 text-lg rounded-xl text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/70 focus:border-primary/40 transition-colors duration-150"
              placeholder="Search players, clubs or leagues..."
              autoFocus
            />
          </div>
        </form>
      </header>

      {loading && (
        <div className="flex items-center gap-3 text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin text-primary">progress_activity</span>
          <span className="text-sm">Searching...</span>
        </div>
      )}

      {!loading && results.length === 0 && searchUrl && (
        <div className="bg-surface-container-low border border-outline-variant/30 rounded-xl text-center py-16 px-6">
          <span className="material-symbols-outlined text-4xl text-outline mb-3 block">search_off</span>
          <p className="font-headline font-bold text-on-surface mb-1">No results</p>
          <p className="text-sm text-on-surface-variant">
            Nothing found for &ldquo;{query}&rdquo; &mdash; check the spelling or try a shorter term.
          </p>
        </div>
      )}

      {!loading && !searchUrl && (
        <div className="bg-surface-container-low border border-outline-variant/30 rounded-xl text-center py-16 px-6">
          <span className="material-symbols-outlined text-4xl text-outline mb-3 block">search</span>
          <p className="font-headline font-bold text-on-surface mb-1">Search Below The Radar</p>
          <p className="text-sm text-on-surface-variant max-w-md mx-auto">
            Find players, clubs and leagues across English non-league Steps 5&ndash;7. Type at least 2 characters to start.
          </p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-8">
          <ResultGroup
            title="Players"
            icon="person"
            items={players}
            renderItem={(p) => (
              <div
                key={p.player_name || p.name}
                onClick={() => navigate(`/players/${encodeURIComponent(p.player_name || p.name)}`)}
                className="relative flex items-center justify-between gap-3 min-h-[64px] bg-surface-container border border-outline-variant/30 p-4 rounded-xl cursor-pointer overflow-hidden hover:bg-surface-container-high hover:border-outline-variant/50 transition-colors duration-150 active:scale-[0.99] group"
              >
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                <div className="flex items-center gap-3 min-w-0">
                  <InitialsAvatar name={p.player_name || p.name} clubName={p.club_name} size="sm" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-on-surface group-hover:text-primary transition-colors duration-150 truncate">{p.player_name || p.name}</p>
                    <p className="text-xs text-on-surface-variant truncate">{p.club_name} &bull; {p.league_name}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-headline font-bold text-on-surface tabular-nums">
                    {p.total_goals || 0} <span className="font-body text-xs font-normal text-on-surface-variant">goals</span>
                  </p>
                  <p className="text-xs font-semibold text-secondary tabular-nums">GPG {p.goals_per_game ? Number(p.goals_per_game).toFixed(2) : '—'}</p>
                </div>
              </div>
            )}
          />

          <ResultGroup
            title="Clubs"
            icon="stadium"
            items={clubs}
            renderItem={(c) => (
              <div
                key={c.club_name || c.name}
                className="flex items-center justify-between gap-3 min-h-[64px] bg-surface-container border border-outline-variant/30 p-4 rounded-xl"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 shrink-0 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-secondary text-lg">stadium</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-on-surface truncate">{c.club_name || c.name}</p>
                    <p className="text-xs text-on-surface-variant truncate">{c.league_name} &bull; {c.region_name}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-headline font-bold text-on-surface tabular-nums">
                    {c.total_club_goals || 0} <span className="font-body text-xs font-normal text-on-surface-variant">goals</span>
                  </p>
                  <p className="text-xs text-on-surface-variant tabular-nums">{c.player_count || 0} players</p>
                </div>
              </div>
            )}
          />

          <ResultGroup
            title="Leagues"
            icon="emoji_events"
            items={leagues}
            renderItem={(l) => (
              <div
                key={l.id || l.league_name || l.name}
                onClick={() => l.id && navigate(`/leagues/${l.id}`)}
                className="relative flex items-center justify-between gap-3 min-h-[64px] bg-surface-container border border-outline-variant/30 p-4 rounded-xl cursor-pointer overflow-hidden hover:bg-surface-container-high hover:border-outline-variant/50 transition-colors duration-150 active:scale-[0.99] group"
              >
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary text-lg">emoji_events</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-on-surface group-hover:text-primary transition-colors duration-150 truncate">{l.league_name || l.name}</p>
                    <p className="text-xs text-on-surface-variant truncate">{l.region_name} &bull; Step {l.step_number}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-on-surface-variant tabular-nums">{l.club_count || 0} clubs &bull; {l.player_count || 0} players</p>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
